import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

// Store
import { Dispatch } from '../../store/store';

// Enums
import { ChangeChannelAction } from '../../enums/action';

// Components
import Modal from '../Modal/Modal';
import SpinnerLoader from '../SpinnerLoader/SpinnerLoader';
import InteractiveButton from '../InteractiveButton/InteractiveButton';
import { DeviceActivityState } from '../DeviceActivityState/DeviceActivityState';

// Types
import { ElementSize } from '../../types/element-size';
import { FaultedCycleGroup } from '../../utils/getFaultedCycleGroup';

// Utils
import formatTimestampFromUnix from '../../store/models/utils/formatTimestampFromUnix';

type ComponentProps = {
  groups: FaultedCycleGroup[];
  isVisible: boolean;
  onClose: () => void;
} & ReturnType<typeof mapDispatch>;

const FaultedCyclesModal: React.FC<ComponentProps> = ({
  groups,
  isVisible,
  onClose,
  getListChannelFaults,
  updateChannels,
}) => {
  const [enrichedGroups, setEnrichedGroups] = useState<FaultedCycleGroup[] | null>(null);
  const [resettingCycleId, setResettingCycleId] = useState<string | null>(null);

  useEffect(() => {
    if (!isVisible) {
      setEnrichedGroups(null);
      return;
    }

    const enrich = async () => {
      const connectedFaultIds = groups.flatMap((group) =>
        group.faults.filter((fault) => fault.isControllerConnected).map((fault) => fault.id),
      );

      if (!connectedFaultIds.length) {
        setEnrichedGroups(groups);
        return;
      }

      const res = await getListChannelFaults({ channel_ids: connectedFaultIds });
      const apiMessagesById = new Map((res?.channelsFaults ?? []).map((item) => [item.pk, item.messages?.value ?? []]));

      setEnrichedGroups(
        groups.map((group) => ({
          ...group,
          faults: group.faults.map((fault) => {
            if (!fault.isControllerConnected) {
              return fault;
            }

            const apiMessages = apiMessagesById.get(fault.id) ?? [];
            return { ...fault, channelFaultMessage: [...(fault.channelFaultMessage ?? []), ...apiMessages] };
          }),
        })),
      );
    };

    enrich();
  }, [isVisible, groups]);

  const handleReset = async (group: FaultedCycleGroup) => {
    setResettingCycleId(group.cycleId);
    try {
      await updateChannels({
        cycleId: group.cycleId,
        data: { action: ChangeChannelAction.RESET_CHANNEL_FAULTS, list: group.resetPayload },
      });
    } finally {
      setResettingCycleId(null);
    }
  };

  return (
    <Modal
      modalTitle="Faulted Cycles"
      isVisible={isVisible}
      onClose={onClose}
      showAcceptBtn={false}
      showCancelBtn={false}
      className="w-[90vw] sm:max-w-[90vw] 2xl:max-w-[90vw]"
    >
      {!enrichedGroups ? (
        <div className="flex w-full items-center justify-center py-7">
          <SpinnerLoader className="h-10 w-10" />
        </div>
      ) : (
        <div className="max-h-[70vh] overflow-y-auto overflow-x-auto text-left">
          <table className="w-full text-xs">
            <thead className="sticky top-0 z-10 bg-white text-slate-gray dark:bg-boxdark dark:text-white">
              <tr>
                <th className="px-2 py-1.5">Channel</th>
                <th className="px-2 py-1.5">Controller</th>
                <th className="px-2 py-1.5">Connection</th>
                <th className="px-2 py-1.5">Fault</th>
                <th className="px-2 py-1.5">Time</th>
              </tr>
            </thead>
            <tbody>
              {enrichedGroups.map((group) => (
                <React.Fragment key={group.cycleId}>
                  <tr className="bg-gray-2 dark:bg-meta-4">
                    <td colSpan={4} className="px-2 py-1.5 font-bold">
                      {group.cycleName}
                    </td>
                    <td className="px-2 py-1.5 text-right">
                      <InteractiveButton
                        size={ElementSize.XS}
                        text={resettingCycleId === group.cycleId ? 'Resetting...' : 'Reset faults'}
                        disabled={resettingCycleId === group.cycleId || !group.resetPayload.length}
                        onClick={() => handleReset(group)}
                      />
                    </td>
                  </tr>
                  {group.faults.map((fault) => (
                    <tr key={fault.id} className="border-b border-stroke dark:border-strokedark">
                      <td className="px-2 py-1">{fault.channelName}</td>
                      <td className="px-2 py-1">{fault.controllerName}</td>
                      <td className="px-2 py-1">
                        <DeviceActivityState active={fault.isControllerConnected} />
                      </td>
                      <td className="px-2 py-1">
                        {[...(fault.controllerFaultMessages ?? []), ...(fault.channelFaultMessage ?? [])].join(', ') || '-'}
                      </td>
                      <td className="px-2 py-1">{formatTimestampFromUnix(fault.timestamp)}</td>
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Modal>
  );
};

const mapDispatch = (dispatch: Dispatch) => ({
  getListChannelFaults: dispatch.channels.getListChannelFaults,
  updateChannels: dispatch.channels.updateChannels,
});

export default connect(null, mapDispatch)(FaultedCyclesModal);
